/**
 * Google Analytics 4 + Google Ads conversion tracking.
 *
 * Configured via environment variables (all optional):
 *   REACT_APP_GA4_ID              - e.g. "G-XXXXXXXXXX"
 *   REACT_APP_GADS_ID             - e.g. "AW-1234567890"
 *   REACT_APP_GADS_LABEL_QUOTE    - Conversion label for "Quote Submitted"
 *   REACT_APP_GADS_LABEL_PHONE    - Conversion label for "Phone Click"
 *
 * The gtag.js snippet is injected once per session on first initAnalytics().
 * Page views on route changes are handled by a small hook (useRouteTracking).
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || '';
const GADS_ID = process.env.REACT_APP_GADS_ID || '';
const GADS_LABEL_QUOTE = process.env.REACT_APP_GADS_LABEL_QUOTE || '';
const GADS_LABEL_PHONE = process.env.REACT_APP_GADS_LABEL_PHONE || '';

let inited = false;

export const initAnalytics = () => {
  if (inited || typeof window === 'undefined') return;
  if (!GA4_ID && !GADS_ID) return; // Nothing configured yet
  inited = true;

  // Load gtag.js using GA4 ID if present, else Ads ID
  const loaderId = GA4_ID || GADS_ID;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${loaderId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  if (GA4_ID) gtag('config', GA4_ID, { send_page_view: true });
  if (GADS_ID) gtag('config', GADS_ID);

  // Global listener: any click on a "tel:" link fires trackPhoneClick.
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest && e.target.closest('a[href^="tel:"]');
    if (a) trackPhoneClick();
  }, true);
};

/** Fire GA4 event and/or Google Ads conversion for a completed quote request. */
export const trackQuoteSubmitted = (extra = {}) => {
  if (!window.gtag) return;
  // GA4 event
  window.gtag('event', 'generate_lead', {
    event_category: 'Quote',
    event_label: 'quote_submitted',
    value: extra.value || 1,
    ...extra,
  });
  // Google Ads conversion
  if (GADS_ID && GADS_LABEL_QUOTE) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_ID}/${GADS_LABEL_QUOTE}`,
      value: extra.value || 1,
      currency: 'USD',
    });
  }
};

/** Fire GA4 event and/or Google Ads conversion when a phone link is clicked. */
export const trackPhoneClick = () => {
  if (!window.gtag) return;
  window.gtag('event', 'phone_click', {
    event_category: 'Contact',
    event_label: 'phone_click',
  });
  if (GADS_ID && GADS_LABEL_PHONE) {
    window.gtag('event', 'conversion', {
      send_to: `${GADS_ID}/${GADS_LABEL_PHONE}`,
    });
  }
};

/** Track a SPA route change in GA4 (page_view). */
export const trackPageView = (path) => {
  if (!window.gtag || !GA4_ID) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const isAnalyticsConfigured = () => Boolean(GA4_ID || GADS_ID);
